/* @flow */
import * as React from 'react';
import { sortBy } from 'ramda';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useMediaQuery } from '@mui/material';

// Hooks
import useSelf from 'src/shared/domain/user/hook/useSelf';

// Utils
import messages from 'src/shared/app/braindate/l10n/braindateL10n';
import { tablet } from '@braindate/ui/lib/token/mediaQuery';
import { getUserId } from '@braindate/domain/lib/user/util';
import { clearModal } from 'src/shared/app/base/modal/slice/modalSlice';
import { useGetTopicQuery } from 'src/shared/app/base/api/endpoint/topicsEndpoint';
import {
  getGroupTopicParticipants,
  getGroupTopic,
} from '@braindate/domain/lib/topic/util';

// Components
import BraindateParticipantsList from './BraindateParticipantsList';
import BraindateParticipantsItemLink from './elements/BraindateParticipantsItemLink';
import BraindateParticipantsTable from './BraindateParticipantsTable';
import BraindateParticipantsItemTopics from './elements/BraindateParticipantsItemTopics';
import BraindateParticipantsItemAvatar from './elements/BraindateParticipantsItemAvatar';
import BraindateParticipantsItemInterests from './elements/BraindateParticipantsItemInterests';

// Types
import type { User } from '@braindate/domain/lib/user/type';

type Props = {
  topicId: number,
};

export type BraindateParticipantListElement = {
  element: React.AbstractComponent<{
    participant: User,
    title?: string,
    isFetching?: boolean,
    onClose?: () => void,
  }>,
  center?: boolean,
  showMobileTitle: boolean,
  title: string,
};

const BraindateParticipants = ({ topicId }: Props) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const self = useSelf();
  const isDesktop = useMediaQuery(tablet);
  const { data: topic } = useGetTopicQuery({ id: topicId });

  const groupTopic = topic && getGroupTopic(topic);
  const participants = groupTopic && getGroupTopicParticipants(groupTopic);
  const sortByAuthorFirst = sortBy((participant) =>
    self && getUserId(participant) === getUserId(self) ? 1 : 0,
  );
  const participantsSorted: User[] = participants
    ? sortByAuthorFirst(participants)
    : [];

  const handleClose = () => dispatch(clearModal());

  const columns: BraindateParticipantListElement[] = [
    {
      title: intl.formatMessage(messages.name),
      showMobileTitle: false,
      element: BraindateParticipantsItemAvatar,
    },
    {
      title: intl.formatMessage(messages.topics),
      showMobileTitle: true,
      element: BraindateParticipantsItemTopics,
      center: true,
    },
    {
      title: intl.formatMessage(messages.interests),
      showMobileTitle: true,
      element: BraindateParticipantsItemInterests,
    },
    {
      title: intl.formatMessage(messages.connect),
      showMobileTitle: false,
      element: BraindateParticipantsItemLink,
    },
  ];

  if (isDesktop) {
    return (
      <BraindateParticipantsTable
        participants={participantsSorted}
        columns={columns}
        onClose={handleClose}
      />
    );
  }

  return (
    <BraindateParticipantsList
      participants={participantsSorted}
      columns={columns}
      onClose={handleClose}
    />
  );
};

export default React.memo<Props>(BraindateParticipants);
